/**
 * State deadlines for notifying a generator status change, from the TPMs'
 * "Generator Status Change Deadlines" page. Tier 1 is the strictest.
 * Only above-reported-status comparisons get a deadline, counted from detection
 * because shipment data arrives weeks after the month it covers.
 */
export type NotificationDeadlineRule = { windowDays: number; tier: 1 | 2 | 3 | 4 };

/** No numeric timeline: Encamp recommends notifying within 30 days. */
export const DEFAULT_NOTIFICATION_DEADLINE: NotificationDeadlineRule = { windowDays: 30, tier: 4 };

/** `promptly`: the state's rule gives no number of days; windowDays is Encamp's. */
type StateRule = NotificationDeadlineRule & { onlyWhenMovingToLqg?: boolean; promptly?: boolean };

const STATE_NOTIFICATION_DEADLINES: Record<string, StateRule> = {
  VA: { windowDays: 0, tier: 1, onlyWhenMovingToLqg: true },
  WA: { windowDays: 0, tier: 1 },
  LA: { windowDays: 7, tier: 1 },
  MA: { windowDays: 30, tier: 1, promptly: true },
  NH: { windowDays: 30, tier: 2 },
  TN: { windowDays: 30, tier: 2 },
  KY: { windowDays: 30, tier: 2 },
  NE: { windowDays: 30, tier: 2 },
  KS: { windowDays: 60, tier: 3 },
  TX: { windowDays: 90, tier: 3 },
};

export function notificationDeadlineRule(
  state: string | null | undefined,
  movingToLqg: boolean
): NotificationDeadlineRule {
  const rule = STATE_NOTIFICATION_DEADLINES[state?.toUpperCase() ?? ""];
  if (!rule || (rule.onlyWhenMovingToLqg && !movingToLqg)) return DEFAULT_NOTIFICATION_DEADLINE;
  return { windowDays: rule.windowDays, tier: rule.tier };
}

export function requiresPromptNotification(state: string | null | undefined): boolean {
  return STATE_NOTIFICATION_DEADLINES[state?.toUpperCase() ?? ""]?.promptly === true;
}

/** SQL for one rule field; callers supply trusted column expressions. */
export function notificationDeadlineSql(
  field: keyof NotificationDeadlineRule,
  columns: { state: string; movingToLqg: string }
): string {
  const branches = Object.entries(STATE_NOTIFICATION_DEADLINES)
    .map(([state, rule]) => {
      const value = rule.onlyWhenMovingToLqg
        ? `CASE WHEN ${columns.movingToLqg} THEN ${rule[field]} ELSE ${DEFAULT_NOTIFICATION_DEADLINE[field]} END`
        : `${rule[field]}`;
      return `WHEN '${state}' THEN ${value}`;
    })
    .join(" ");
  return `(CASE UPPER(${columns.state}) ${branches} ELSE ${DEFAULT_NOTIFICATION_DEADLINE[field]} END)`;
}

export type NotificationDeadlineStatus = "IMMEDIATE" | "OVERDUE" | "DUE_SOON" | "ON_TRACK";

/** The last quarter of a window counts as nearing the deadline. */
const DUE_SOON_FRACTION = 0.25;

export function notificationDeadlineStatus(
  windowDays: number,
  daysRemaining: number
): NotificationDeadlineStatus {
  if (windowDays === 0) return "IMMEDIATE";
  if (daysRemaining < 0) return "OVERDUE";
  if (daysRemaining <= Math.ceil(windowDays * DUE_SOON_FRACTION)) return "DUE_SOON";
  return "ON_TRACK";
}
